import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';

import { api } from '../lib/apiClient';

/**
 * The copy layer, client side.
 *
 * ── THE DEFAULT IS THE PRODUCT; THE OVERRIDE IS THE EXCEPTION ────────────────────────
 *
 *   t('events.detail.cta.primary', 'Get tickets')
 *
 * The second argument is not a convenience — it is the string the product ships with, and
 * it is what renders unless somebody has deliberately overridden that key. There is no
 * code path here in which a missing bundle, a failed request, a 500, an offline browser,
 * a malformed payload or an unknown key produces anything other than that literal. A copy
 * outage degrades to the product as it was, never to a blank heading or an unlabelled
 * button.
 *
 * That is why the bundle contains ONLY overridden keys: "absent" is the normal case, and
 * the fallback is exercised on every render of every unoverridden string rather than
 * living on a rarely-taken branch.
 *
 * ── RENDERING IS NEVER BLOCKED ON COPY ───────────────────────────────────────────────
 *
 * The provider renders its children immediately and fills copy in when it arrives. A page
 * is never held back, and there is no spinner: the first paint shows the code defaults,
 * which are correct words, so an override arriving a moment later is the only thing that
 * can change — and only for keys somebody deliberately reworded.
 *
 * ── PLAIN TEXT ONLY ──────────────────────────────────────────────────────────────────
 *
 * Values are returned as strings and rendered by React as text. Nothing here produces
 * markup, and no caller may pass one of these into dangerouslySetInnerHTML.
 */

export type CopyBundle = Record<string, string>;

/** Which surface's copy to resolve. The server derives the rest of the chain by id. */
export interface CopyScope {
  eventId?: string | null;
  venueId?: string | null;
  venueKind?: string | null;
  organizationId?: string | null;
}

interface CopyContextValue {
  bundle: CopyBundle;
  /** True once a fetch has settled, either way. Nothing should gate rendering on it. */
  ready: boolean;
}

const EMPTY: CopyContextValue = { bundle: {}, ready: false };
const CopyContext = createContext<CopyContextValue>(EMPTY);

function scopeQuery(scope: CopyScope): string {
  const q = new URLSearchParams();
  if (scope.eventId) q.set('eventId', scope.eventId);
  if (scope.venueId) q.set('venueId', scope.venueId);
  if (scope.venueKind) q.set('venueKind', scope.venueKind);
  if (scope.organizationId) q.set('organizationId', scope.organizationId);
  const s = q.toString();
  return s ? `?${s}` : '';
}

/**
 * Fetches one bundle for the given scope.
 *
 * Nested providers are supported and MERGE: an event-scoped provider inside the platform
 * one sees both, with the inner scope winning. That keeps a page from having to refetch
 * platform-wide wording it already has.
 */
export function CopyProvider({ scope = {}, children }: { scope?: CopyScope; children: ReactNode }) {
  const parent = useContext(CopyContext);
  const [bundle, setBundle] = useState<CopyBundle>({});
  const [ready, setReady] = useState(false);

  const query = scopeQuery(scope);
  // Guards against an older response landing after a newer one and reverting the copy.
  const token = useRef(0);

  useEffect(() => {
    const mine = ++token.current;
    let cancelled = false;
    setReady(false);

    api.get<{ copy: CopyBundle }>(`/copy/bundle${query}`)
      .then((res) => {
        if (cancelled || mine !== token.current) return;
        // A payload that is not a flat object of strings is discarded rather than used.
        // Corrupt copy must never become product copy.
        const next = res && typeof res.copy === 'object' && res.copy !== null ? res.copy : {};
        const clean: CopyBundle = {};
        for (const [k, v] of Object.entries(next)) if (typeof v === 'string') clean[k] = v;
        setBundle(clean);
      })
      .catch(() => {
        // THE WHOLE POINT. Network error, 500, 404, offline, blocked — the bundle stays
        // empty and every call site renders its own default.
        if (!cancelled && mine === token.current) setBundle({});
      })
      .finally(() => { if (!cancelled && mine === token.current) setReady(true); });

    return () => { cancelled = true; };
  }, [query]);

  const value = useMemo(
    () => ({ bundle: { ...parent.bundle, ...bundle }, ready: parent.ready || ready }),
    [parent.bundle, parent.ready, bundle, ready],
  );

  return <CopyContext.Provider value={value}>{children}</CopyContext.Provider>;
}

/** Named placeholders only. No expressions, no property paths, no evaluation. */
const PLACEHOLDER = /\{([a-zA-Z][a-zA-Z0-9_]*)\}/g;

export function substitute(template: string, variables?: Record<string, string | number>): string {
  if (!variables) return template;
  return template.replace(PLACEHOLDER, (match, name: string) =>
    // Own properties only: `variables[name]` would resolve {constructor} and {toString}
    // through the prototype chain and print runtime internals into the page.
    (Object.hasOwn(variables, name) ? String(variables[name]) : match));
}

export type CopyFn = (
  key: string,
  codeDefault: string,
  variables?: Record<string, string | number>,
) => string;

/**
 * `t(key, codeDefault, vars?)`.
 *
 * Safe outside a provider: with no provider the context is an empty bundle, so every key
 * resolves to its default. A component can therefore adopt copy without its whole tree
 * having to be wrapped first, which is what makes adoption incremental.
 */
export function useCopy(): CopyFn {
  const { bundle } = useContext(CopyContext);

  return useCallback((key, codeDefault, variables) => {
    const value = bundle[key];
    const chosen = typeof value === 'string' && value.trim() !== '' ? value : codeDefault;
    return substitute(chosen, variables);
  }, [bundle]);
}
